/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { AxiosError, AxiosResponse } from "axios";
import { merge } from "lodash";

import apiClient from "../../../services/api-client";
import { getProductByMasterProductId } from "../../typesense/api";
import { TypesenseProduct } from "../../typesense/types";
import {
  getProductDetails,
  LocationState,
} from "../components/ProductDetails/utils/getProductDetails";
import { ProductDetailsApiResponse } from "../storage";

import { getDefaultProduct, ProductVariant } from "./getDefaultDimension";

export const fetchProductDetail = async (
  masterProductId: string,
  accountNumber?: string,
  branchId?: string,
  size?: string | null | undefined,
  colour?: string | null | undefined,
  state?: LocationState | undefined
): Promise<ProductDetailsApiResponse> => {
  return new Promise((resolve, reject) => {
    try {
      const promises = [];
      promises.push(getDefaultProduct(masterProductId, size ?? "", colour ?? ""));

      promises.push(
        apiClient.get<ProductDetailsApiResponse, AxiosError>(
          `/products/${masterProductId}?accountNumber=${accountNumber}&branchId=${branchId}`
        )
      );
      promises.push(getProductByMasterProductId(Number(masterProductId)));

      Promise.all(promises)
        .then((results) => {
          const product = results[0] as ProductVariant;
          const response = results[1] as AxiosResponse<ProductDetailsApiResponse>;
          const searchProduct = results[2] as TypesenseProduct | undefined;

          if (response?.status.toString().startsWith("2")) {
            if (searchProduct?.stock) {
              searchProduct.stockAvailability = searchProduct?.stock;
            }

            const data = response.data;

            const newItem = merge(data, searchProduct, {
              masterProductId: Number(masterProductId),
              productId: product?.productId ?? 0,
              sizeDescription: product?.size ?? "",
              colourDescription: product?.colour ?? "",
              lifeCycleState: searchProduct?.lifecycle,
              isPriceNegotiable: searchProduct?.price_negotiable,
              hasVariants: searchProduct?.has_variants,
              categories: searchProduct?.categories,
              category: searchProduct?.l1_category,
              subCategory: searchProduct?.l2_category,
            });
            if (state) {
              getProductDetails(searchProduct, state).then(
                ({ category, subCategory, isPriceNegotiable }) => {
                  resolve({
                    ...newItem,
                    category,
                    subCategory,
                    isPriceNegotiable,
                  });
                }
              );
            } else {
              resolve(newItem);
            }
          }
        })
        .catch((err) => {
          if (err instanceof Error) {
            reject(err);
          } else {
            reject(new Error(String(err)));
          }
        });
    } catch (err) {
      if (err instanceof Error) {
        reject(err);
      } else {
        reject(new Error(String(err)));
      }
    }
  });
};
